import React, { useEffect, useRef, useState } from "react";
import { Reorder, useDragControls } from "framer-motion";
import { api, coverUrl } from "@/lib/api";
import { toast } from "sonner";
import { GripVertical, Pencil, Trash2 } from "lucide-react";

const RECENT_LIMIT = 20;
const SECTIONS = [["remix", "Remixes"], ["mashup", "Mashups"], ["original", "Originales"], ["pool", "Pool"]];
const inSection = (t, key) => t.category === key || (t.sections || []).includes(key);

export function SortableTrackList({ tracks, onEdit, onDelete, onReordered }) {
  const [view, setView] = useState("all");
  const [items, setItems] = useState(tracks);
  const [selected, setSelected] = useState([]);
  const latest = useRef(tracks);
  const dirty = useRef(false);

  useEffect(() => { setItems(tracks); latest.current = tracks; setSelected((s) => s.filter((id) => tracks.some((t) => t.id === id))); }, [tracks]);

  const recent = [...tracks].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)).slice(0, RECENT_LIMIT);
  const [genreFilter, setGenreFilter] = useState("");
  const [search, setSearch] = useState("");
  const gq = genreFilter.trim().toLowerCase();
  const sq = search.trim().toLowerCase();
  const sortable = view !== "recent" && !gq && !sq;
  const genreList = [...new Set(tracks.flatMap((t) => (t.genre || "").split(",").map((g) => g.trim()).filter(Boolean)))].sort((a, b) => a.localeCompare(b));
  const inGenre = (t) => !gq || (gq === "-" ? !(t.genre || "").trim() : (t.genre || "").toLowerCase().includes(gq));
  const matches = (t) => !sq || `${t.title} ${t.artist || ""}`.toLowerCase().includes(sq);
  const visible = (view === "all" ? items : view === "recent" ? recent : items.filter((t) => inSection(t, view))).filter(inGenre).filter(matches);
  const allSelected = visible.length > 0 && visible.every((t) => selected.includes(t.id));
  const toggleAll = () => setSelected(allSelected ? selected.filter((id) => !visible.some((t) => t.id === id)) : [...new Set([...selected, ...visible.map((t) => t.id)])]);
  const toggleOne = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const bulkDelete = async () => {
    if (!selected.length || !window.confirm(`¿Eliminar ${selected.length} pista${selected.length === 1 ? "" : "s"}? Esta acción no se puede deshacer.`)) return;
    try {
      await api.post("/admin/tracks/bulk-delete", { ids: selected });
      toast.success(`${selected.length} pista${selected.length === 1 ? "" : "s"} eliminada${selected.length === 1 ? "" : "s"}`);
      setSelected([]);
      onReordered();
    } catch { toast.error("No se pudieron eliminar"); }
  };

  const [genreInput, setGenreInput] = useState("");
  const [genreMode, setGenreMode] = useState("replace");
  const applyGenre = async (e) => {
    e?.preventDefault();
    if (!selected.length || !genreInput.trim()) return;
    try {
      await api.post("/admin/tracks/bulk-genre", { ids: selected, genre: genreInput.trim(), mode: genreMode });
      toast.success(`Género "${genreInput.trim()}" aplicado a ${selected.length} pista${selected.length === 1 ? "" : "s"}`);
      onReordered();
    } catch { toast.error("No se pudo aplicar el género"); }
  };

  const handleReorder = (next) => {
    const merged = view === "all" ? next : (() => { let i = 0; return latest.current.map((t) => (inSection(t, view) ? next[i++] : t)); })();
    setItems(merged); latest.current = merged; dirty.current = true;
  };

  const persist = async () => {
    if (!dirty.current) return;
    dirty.current = false;
    try {
      const ids = (view === "all" ? latest.current : latest.current.filter((t) => inSection(t, view))).map((t) => t.id);
      await api.put("/admin/tracks/reorder", { ids });
      toast.success("Orden guardado");
      onReordered();
    } catch {
      toast.error("No se pudo guardar el orden");
    }
  };

  const tabCls = (a) => `px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-widest border transition-colors ${a ? "bg-red-600 border-red-600 text-white" : "border-white/15 text-white/60 hover:text-white hover:border-white/40"}`;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap" data-testid="admin-track-list-toolbar">
        <button type="button" onClick={() => setView("all")} className={tabCls(view === "all")} data-testid="admin-track-view-all">Todas ({tracks.length})</button>
        {SECTIONS.map(([key, label]) => (
          <button key={key} type="button" onClick={() => setView(key)} className={tabCls(view === key)} data-testid={`admin-track-view-${key}`}>{label} ({tracks.filter((t) => inSection(t, key)).length})</button>
        ))}
        <button type="button" onClick={() => setView("recent")} className={tabCls(view === "recent")} data-testid="admin-track-view-recent">Más recientes ({recent.length})</button>
        <div className="relative flex items-center" data-testid="admin-track-search-wrap">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar título o artista…" data-testid="admin-track-search"
            className={`bg-[#16161D] border rounded-full pl-3 pr-7 py-1.5 text-xs text-white outline-none w-52 ${sq ? "border-red-600/60" : "border-white/10 focus:border-red-600/60"}`} />
          {sq && <button type="button" onClick={() => setSearch("")} data-testid="admin-track-search-clear" className="absolute right-2 text-white/40 hover:text-white text-xs">✕</button>}
        </div>
        <div className="relative flex items-center" data-testid="admin-track-genre-filter-wrap">
          <input list="admin-genre-options" value={genreFilter} onChange={(e) => setGenreFilter(e.target.value)} placeholder="Filtrar por género… (- = sin género)" data-testid="admin-track-genre-filter"
            className={`bg-[#16161D] border rounded-full pl-3 pr-7 py-1.5 font-mono text-[10px] uppercase tracking-widest text-white outline-none w-56 ${gq ? "border-red-600/60" : "border-white/10 focus:border-red-600/60"}`} />
          <datalist id="admin-genre-options">{genreList.map((g) => <option key={g} value={g} />)}</datalist>
          {gq && <button type="button" onClick={() => setGenreFilter("")} data-testid="admin-track-genre-filter-clear" className="absolute right-2 text-white/40 hover:text-white text-xs">✕</button>}
        </div>
        {gq && <span className="font-mono text-[10px] uppercase tracking-widest text-red-400" data-testid="admin-track-genre-filter-count">{visible.length} con "{genreFilter.trim()}"</span>}
        <span className="flex-1" />
        <label className="flex items-center gap-2 text-xs text-white/60 cursor-pointer">
          <input type="checkbox" checked={allSelected} onChange={toggleAll} className="accent-red-600" data-testid="admin-track-select-all" />
          Seleccionar todo
        </label>
        {selected.length > 0 && (
          <button type="button" onClick={bulkDelete} data-testid="admin-track-bulk-delete"
            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-red-600/20 text-red-400 hover:bg-red-600/30">
            <Trash2 className="w-3.5 h-3.5" /> Eliminar ({selected.length})
          </button>
        )}
      </div>
      {selected.length > 0 && (
        <form onSubmit={applyGenre} className="flex items-center gap-2 p-2 rounded-xl border border-red-900/40 bg-red-950/10" data-testid="admin-track-genre-bar">
          <span className="font-mono text-[10px] uppercase tracking-widest text-red-400 shrink-0">{selected.length} seleccionada{selected.length === 1 ? "" : "s"} · género</span>
          <input value={genreInput} onChange={(e) => setGenreInput(e.target.value)} placeholder="Escribe el género(s) y pulsa Enter" autoFocus data-testid="admin-track-genre-input"
            className="flex-1 bg-[#16161D] border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white outline-none focus:border-red-600/60" />
          <select value={genreMode} onChange={(e) => setGenreMode(e.target.value)} data-testid="admin-track-genre-mode" className="bg-[#16161D] border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white">
            <option value="replace">Reemplazar</option><option value="add">Añadir</option>
          </select>
          <button type="submit" data-testid="admin-track-genre-apply" className="btn-red text-white text-xs px-3 py-1.5 rounded-lg font-semibold">Aplicar</button>
        </form>
      )}
      {view === "recent" ? <div className="text-[11px] text-white/40 font-mono uppercase tracking-widest">Últimas {RECENT_LIMIT} subidas · orden fijo por fecha</div>
        : view !== "all" && <div className="text-[11px] text-white/40 font-mono uppercase tracking-widest">Arrastra para ordenar cómo aparecen en la sección {SECTIONS.find(([k]) => k === view)?.[1]}</div>}
      <Reorder.Group axis="y" values={visible} onReorder={sortable ? handleReorder : () => {}} layoutScroll
        className="space-y-2 max-h-[700px] overflow-y-auto pr-2" data-testid="sortable-track-list">
        {visible.map((t, i) => (
          <SortableRow key={t.id} track={t} index={i} onEdit={onEdit} onDelete={onDelete} onDragEnd={persist}
            draggable={sortable} selected={selected.includes(t.id)} onToggle={() => toggleOne(t.id)} />
        ))}
      </Reorder.Group>
    </div>
  );
}

function SortableRow({ track: t, index, onEdit, onDelete, onDragEnd, draggable, selected, onToggle }) {
  const controls = useDragControls();
  return (
    <Reorder.Item value={t} dragListener={false} dragControls={controls} onDragEnd={onDragEnd}
      data-testid={`sortable-track-${t.id}`}
      className={`bg-[#111116] border rounded-lg p-3 flex items-center gap-3 select-none ${selected ? "border-red-800/60 bg-red-950/10" : "border-white/5"}`}>
      <input type="checkbox" checked={!!selected} onChange={onToggle} className="accent-red-600" data-testid={`admin-track-select-${t.id}`} />
      {draggable ? (
        <button type="button" onPointerDown={(e) => controls.start(e)} data-testid={`drag-handle-${t.id}`}
          className="text-white/30 hover:text-white cursor-grab active:cursor-grabbing touch-none p-1 -ml-1">
          <GripVertical className="w-4 h-4" />
        </button>
      ) : <span className="w-6" />}
      <span className="font-mono text-[10px] text-white/30 w-5 text-right" data-testid={`track-position-${t.id}`}>{index + 1}</span>
      <div className="w-10 h-10 rounded overflow-hidden bg-neutral-900 shrink-0">
        {t.cover_path && <img src={coverUrl(t.cover_path, 160)} alt="" className="w-full h-full object-cover pointer-events-none" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold truncate">{t.title}</div>
        <div className="text-xs text-white/40 truncate">{t.artist} · {t.category} · {t.price === 0 ? "Free" : `€${t.price.toFixed(2)}`} · ▶ {t.play_count || 0} · ⤓ {t.download_count || 0}{t.created_at ? ` · ${new Date(t.created_at).toLocaleDateString()}` : ""}</div>
      </div>
      <button onClick={() => onEdit(t)} data-testid={`admin-edit-track-${t.id}`}
        className="text-white/60 hover:text-white hover:bg-white/5 p-2 rounded-lg"><Pencil className="w-4 h-4" /></button>
      <button onClick={() => onDelete(t.id)} data-testid={`admin-delete-track-${t.id}`}
        className="text-red-500 hover:bg-red-600/10 p-2 rounded-lg"><Trash2 className="w-4 h-4" /></button>
    </Reorder.Item>
  );
}
